import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from './ui';
import { nameProblem } from '../utils/nameRules';

/**
 * Backwards-compatible RenameDialog wrapper around the Modal primitive.
 *
 * Public props (unchanged):
 *   isOpen, onConfirm, onCancel, title, currentName, placeholder
 * Plus `confirmLabel` for reuse as a generic name dialog (defaults to
 * "Rename"; e.g. "Save" when naming a new Design Package), and
 * `overSlideInPanel` for callers inside a slide-in panel — see Modal.
 *
 * Preserves: Enter-to-submit, autoFocus on the input, maxLength=100,
 * and the same client-side validation (non-empty, different from current,
 * <= 100 chars). `requireChange={false}` drops "different from current" for
 * a dialog that names a COPY, where keeping the source's name is the usual
 * answer.
 */
function RenameDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  currentName,
  placeholder,
  confirmLabel = 'Rename',
  overSlideInPanel = false,
  requireChange = true,
}) {
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setNewName(currentName || '');
      setError('');
    }
  }, [isOpen, currentName]);

  const handleConfirm = () => {
    const problem = nameProblem(newName, { currentName, requireChange });
    if (problem) {
      setError(problem);
      return;
    }
    onConfirm(newName.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
    // Escape is handled by Modal primitive via onClose -> onCancel.
  };

  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title={title || 'Rename'}
      size="sm"
      overSlideInPanel={overSlideInPanel}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <Input
        type="text"
        value={newName}
        onChange={(e) => {
          setNewName(e.target.value);
          setError('');
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Enter new name'}
        autoFocus
        maxLength={100}
        error={error || undefined}
      />
    </Modal>
  );
}

export default RenameDialog;
